import { Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import finance from "financejs";
import { millisToDays, toSpanishDate } from "../../../helpers/dateFormat";
import {
  numberFormat,
  participationsFormat,
} from "../../../helpers/numberFormat";

export const DetailLatentStartup = ({ row }) => {
  const { amount, participationValuation, newAmount } = row;
  const shares = amount / participationValuation;
  const { t } = useTranslation("global");

  const dividendsSum = (row.dividends || [])
    .map((div) => div.amount)
    .reduce((a, b) => a + b, 0);

  const tir = () => {
    const cashflows = [];
    const today = new Date().toISOString().split("T")[0];

    // 1. Initial outflow (negative)
    cashflows.push({
      cashflow: -row.amount,
      date: new Date(row.investmentDate)
    });

    // 2. Dividends (positive)
    if (row.dividends && row.dividends.length > 0) {
      for (const div of row.dividends) {
        cashflows.push({
          cashflow: div.amount,
          date: new Date(div.dividendDate)
        });
      }
    }

    // 3. Terminal value (positive)
    const sharesAmount = row.participationValuation
      ? row.amount / row.participationValuation
      : 0;
    const newCapital = sharesAmount * row.newAmount;
    const finalValue = row.investmentType === "Préstamo Convertible" ? row.amount : (newCapital || row.amount);

    cashflows.push({
      cashflow: finalValue,
      date: new Date(today)
    });

    // Group cashflows by date
    const groupedCashflows = {};
    for (const flow of cashflows) {
      const dateStr = flow.date.toISOString().split("T")[0];
      if (!groupedCashflows[dateStr]) {
        groupedCashflows[dateStr] = 0;
      }
      groupedCashflows[dateStr] += flow.cashflow;
    }

    const finalCashflowList = Object.keys(groupedCashflows).map(dateStr => {
      return {
        cashflow: groupedCashflows[dateStr],
        date: new Date(dateStr)
      };
    });

    const financeInstance = new finance();
    try {
      const tirVal = financeInstance.XIRR(
        finalCashflowList.map((f) => f.cashflow),
        finalCashflowList.map((f) => f.date),
        -0.25
      );
      return isNaN(tirVal) || tirVal === Infinity
        ? "0,00%"
        : tirVal.toLocaleString("es-ES", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + "%";
    } catch (error) {
      console.error("Error al calcular la TIR (XIRR) en DetailLatentStartup:", error);
      return "-";
    }
  };

  const moic = () => {
    const sharesAmount = row.participationValuation
      ? row.amount / row.participationValuation
      : 0;
    const newCapital = sharesAmount * row.newAmount;
    const m2mVal = row.participationValuation ? newCapital : row.amount;
    const finalM2M = row.investmentType === "Préstamo Convertible" ? row.amount : m2mVal;

    const moicVal = (finalM2M + dividendsSum) / row.amount;

    return isNaN(moicVal) || moicVal === Infinity
      ? "1,00 x"
      : numberFormat(moicVal).slice(0, -1) + " x";
  };

  return (
    <>
      <Divider sx={{ margin: "15px 0" }} />
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1}
        justifyContent="space-between"
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.investmentDate")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {toSpanishDate(row.investmentDate)}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.roundNumber")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {row.roundNumber}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.amount")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {numberFormat(amount)}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.participationValuation")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {participationValuation ||
            row.investmentType !== "Préstamo Convertible"
              ? numberFormat(participationValuation)
              : "-"}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.shares")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {participationValuation &&
            row.investmentType !== "Préstamo Convertible"
              ? participationsFormat(shares.toFixed(2))
              : "-"}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.newAmount")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {participationValuation &&
            row.investmentType !== "Préstamo Convertible"
              ? numberFormat(newAmount)
              : "-"}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.capitalGain")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {participationValuation &&
            row.investmentType !== "Préstamo Convertible"
              ? numberFormat((shares * newAmount - amount).toFixed(2))
              : "-"}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.dividends")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {numberFormat(dividendsSum)}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.tir")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {tir()}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.moic")}:
          </Typography>
          <Typography variant="caption" color="secondary">
            {moic()}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.vehicle")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {row.vehicle}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          md={{ justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="primary" marginRight="10px">
            {t("positions.whoInvests")}:{" "}
          </Typography>
          <Typography variant="caption" color="secondary">
            {row.whoInvests}
          </Typography>
        </Stack>
      </Stack>
    </>
  );
};
